import "./App.css";
import EmployeeAttendance from "./pages/employeeAttendance";
import LeaveApplication from "./pages/leaveApplication";
import Attendance from "./pages/attendance";
import NavBar from "./pages/NavBar";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./components/AuthContext.js";
import Login from "./pages/Login";
import Footer from "./pages/Footer.js";
import Notifications from "./pages/Notifications.js";
import AdminDashboard from "./pages/AdminDashBoard.js";
import MembersProfile from "./pages/MembersProfile.js";
import AttendanceTracker from "./pages/AttendanceTracker.js";
import AdminNotifications from "./pages/AdminNotifications.js";
import Calendar from "./pages/Calendar.js";
import ManualAdjustment from "./pages/ManualAdjustment.js";
import Profile from "./pages/Profile.js";
import EmpReports from "./pages/EmpReports.js";
import AdminSettings from "./pages/AdminSettings.js";
import OutsourceDashboard from "./pages/OutsourceDashboard.js";

// ProtectedRoute: handles loading + auth + role checks.
// Must be INSIDE the Router tree so Navigate works.
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, role, loading } = useAuth();

  if (loading) {
    return <div className="app-loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(role)) {
    if (role === "admin") {
      return <Navigate to="/admin-dashboard" replace />;
    } else if (role === "outsource") {
      return <Navigate to="/outsource-dashboard" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

function App() {
  const { isAuthenticated, role, loading } = useAuth();

  // IMPORTANT: Router is ALWAYS mounted immediately.
  // Never block Router rendering with a loading check � Login uses useNavigate()
  // which requires Router context to exist. Loading is handled per-route instead.
  return (
    <Router>
      {!loading && isAuthenticated && <NavBar role={role} />}
      <Routes>
        <Route path="/" element={<Login />} />

        {/* Employee-only routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeAttendance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <LeaveApplication />
            </ProtectedRoute>
          }
        />
        <Route
          path="/report"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <Attendance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <Notifications />
            </ProtectedRoute>
          }
        />

        {/* Outsource-only routes */}
        <Route
          path="/outsource-dashboard"
          element={
            <ProtectedRoute allowedRoles={["outsource"]}>
              <OutsourceDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/outsource-reports"
          element={
            <ProtectedRoute allowedRoles={["outsource"]}>
              <Attendance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/outsource-notifications"
          element={
            <ProtectedRoute allowedRoles={["outsource"]}>
              <Notifications />
            </ProtectedRoute>
          }
        />

        {/* Admin-only routes */}
        <Route
          path="/admin-dashboard"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/members-profile"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <MembersProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manual-adjust"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <ManualAdjustment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-notification"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminNotifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Calendar />
            </ProtectedRoute>
          }
        />
        <Route
          path="/emp-reports"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <EmpReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-settings"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminSettings />
            </ProtectedRoute>
          }
        />

        {/* Shared routes */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute allowedRoles={["admin", "employee", "outsource"]}>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute allowedRoles={["admin", "employee"]}>
              <AttendanceTracker />
            </ProtectedRoute>
          }
        />
      </Routes>
      {!loading && isAuthenticated && <Footer />}
    </Router>
  );
}

export default () => (
  <AuthProvider>
    <App />
  </AuthProvider>
);
